import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useSpring,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { Link } from "react-router-dom";
import logo from "../../../wbclogo.png";
import heroBanner from "./banner1.jpeg";
import asset1 from "./assets/1.jpg";
import asset2 from "./assets/2.jpg";
import asset3 from "./assets/3.jpg";
import asset5 from "./assets/5.jpg";
import asset6 from "./assets/6.jpg";
import asset7 from "./assets/7.jpg";
import RegisterModal from "./RegisterModal";

const SITE = "https://www.wbcamateurmuaythaiindia.com";
const RULES_PDF = `${process.env.PUBLIC_URL || ""}/wbc-competition-rules.pdf`;

const categories = [
  { code: "U/10", name: "Under 10", hook: "First steps into the arena — pure heart, pure technique." },
  { code: "U/12", name: "Youth 10–11", hook: "Fast feet, fearless spirit — the future of Indian Muaythai." },
  { code: "U/14", name: "Youth 12–13", hook: "Discipline meets hunger — every round builds your name." },
  { code: "U/16", name: "Youth 14–15", hook: "Serious skill, national spotlight — earn your corner's respect." },
  { code: "U/18", name: "Youth 16–17", hook: "One tier below the pros — fight like your passport depends on it." },
  { code: "SNRS", name: "Senior 18–40", hook: "Elite amateur combat — where reputations are sealed in sweat." },
];

const kitItems = [
  { icon: "T", text: "Event-Exclusive Fighter T-Shirt" },
  { icon: "S", text: "Competition Sandos (Vests)" },
  { icon: "M", text: "Official Muaythai Shorts" },
  { icon: "C", text: "Official Certificate of Participation" },
  { icon: "M", text: "Medal for All Finalists & Champions" },
];

const docs = [
  "Valid Aadhaar Card (ID proof for all athletes)",
  "Completed Medical Declaration Form",
  "Parent/Guardian Consent (athletes below 18 years)",
  "Fighter's Self Consent Form (18 years & above)",
  "Team Entry with Weight Form (submitted by State Head)",
  "State Team Confirmation by State Chairman",
  "Blood test reports (HIV, HBV, HCV) if athlete is 16+",
];

const timeline = [
  {
    day: "20",
    badge: "Intake · separate day",
    title: "Paperwork · Medical · Official Weigh-In",
    subtitle: "Happens on its own day — before the championship dates.",
    body:
      "July 20 is reserved for registration formalities, medical clearance, and weigh-in only — not exhibition or bracket bouts. Exact reporting times, document checklist, and venue flow will be sent automatically in your confirmation email once your entry is approved — watch your inbox.",
    tags: ["Not a fight day", "Email has your checklist", "Mandatory"],
  },
  {
    day: "21",
    badge: "Championship · Day 1",
    title: "Opening Ceremony · First Bell",
    subtitle: "The championship runs 21–23 July — this is where the arena ignites.",
    body:
      "Walk in cleared from weigh-in and march under the lights: ceremony, colours, and the first slate of championship bouts. From here it's three straight days at Talkatora until finals — pace saved for nobody.",
    tags: ["Ceremony + bouts", "Youth & senior", "Bring the noise"],
  },
  {
    day: "22",
    badge: "Championship · Day 2",
    title: "Full Card · Momentum Builds",
    subtitle: "Middle day — brackets tighten and legends emerge.",
    body:
      "Non-stop competition across divisions. Every win redraws the map toward the podium; every loss is written in chalk. This is where crowds learn your name.",
    tags: ["Full schedule", "Bracket drama", "Prime fights"],
  },
  {
    day: "23",
    badge: "Championship · Day 3",
    title: "Finals · Medals · Closing",
    subtitle: "Gold doesn't ask permission.",
    body:
      "Championship finals, medal ceremonies for every category, closing honours for teams and officials — leave with hardware or leave hungry for 2027.",
    tags: ["Finals", "Medals", "Closing ceremony"],
  },
];

const keyDates = [
  { when: "May 15", line: "Team confirmation" },
  { when: "Jul 20", line: "Paperwork · weigh-in" },
  { when: "Jul 21", line: "Opening · Day 1" },
  { when: "Jul 22", line: "Day 2" },
  { when: "Jul 23", line: "Finals · Closing" },
];

const stats = [
  { num: "20 Jul", label: "Weigh-In Day" },
  { num: "21–23 Jul", label: "Championship" },
  { num: "Talkatora", label: "New Delhi" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 28, filter: "blur(6px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

const timelineListVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.16, delayChildren: 0.05 },
  },
};

const timelineItemVariants = {
  hidden: { opacity: 0, y: 36, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  },
};

function Marquee() {
  const items = [
    "WBC Amateur Muaythai India",
    "Open Championship 2026",
    "21 – 23 July 2026",
    "Talkatora Stadium",
    "New Delhi · India",
    "Step Into The Ring",
  ];
  const row = (
    <div className="flex shrink-0 items-center gap-10 pr-10 md:gap-14 md:pr-14">
      {items.map((t, i) => (
        <React.Fragment key={i}>
          <span className="font-bebas text-2xl tracking-[0.06em] text-white/90 md:text-4xl">
            {t}
          </span>
          <span className="text-lg text-wbc-gold-light md:text-xl">◆</span>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="relative overflow-hidden border-y border-white/[0.06] bg-[#0A0204] py-5 md:py-6">
      <motion.div
        className="flex w-max will-change-transform"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 38, repeat: Infinity, ease: "linear" }}
      >
        {row}
        {row}
      </motion.div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-[#0A0204] to-transparent md:w-32" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#0A0204] to-transparent md:w-32" />
    </div>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.3,
  });
  return (
    <motion.div
      className="fixed inset-x-0 top-0 z-[60] h-[2px] origin-left bg-gradient-to-r from-wbc-red via-wbc-gold-light to-wbc-red"
      style={{ scaleX }}
      aria-hidden
    />
  );
}

function ImgPh({ label, sub, className = "" }) {
  return (
    <div
      className={`group relative flex min-h-[96px] flex-col items-center justify-center overflow-hidden rounded border border-white/10 bg-white/[0.02] p-6 text-center transition duration-300 hover:border-white/25 ${className}`}
    >
      <div className="font-condensed text-[10px] font-semibold uppercase tracking-[3px] text-white/40">{label}</div>
      {sub && <p className="mt-1 max-w-[220px] font-barlow text-[11px] leading-snug tracking-wide text-white/30">{sub}</p>}
    </div>
  );
}

function Navbar({ onRegister }) {
  const { scrollY } = useScroll();
  const [scrolled, setScrolled] = useState(false);
  useMotionValueEvent(scrollY, "change", (y) => {
    setScrolled(y > 80);
  });

  return (
    <nav
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "border-b border-white/[0.06] bg-[#060606]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 md:px-10 md:py-4">
        <Link to="/" className="block transition hover:opacity-90">
          <img
            src={logo}
            alt="WBC Amateur Muaythai India"
            className="h-9 w-auto object-contain md:h-11"
          />
        </Link>
        <button
          type="button"
          onClick={onRegister}
          className="font-condensed inline-flex items-center rounded-full bg-wbc-red px-5 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white shadow-[0_10px_30px_rgba(214,0,28,0.35)] transition hover:bg-wbc-red-bright md:px-7 md:py-2.5 md:text-xs md:tracking-[0.24em]"
        >
          Register Now
        </button>
      </div>
    </nav>
  );
}


export default function WbcLanding() {
  const [registerOpen, setRegisterOpen] = useState(false);
  const openRegister = () => setRegisterOpen(true);
  const closeRegister = () => setRegisterOpen(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const prev = window.history.scrollRestoration;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);
    return () => {
      if ("scrollRestoration" in window.history && prev) {
        window.history.scrollRestoration = prev;
      }
    };
  }, []);

  const scheduleRef = useRef(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: scheduleRef,
    offset: ["start 0.78", "end 0.22"],
  });
  const spineProgress = useSpring(scrollYProgress, {
    stiffness: 90,
    damping: 32,
    mass: 0.45,
  });
  const parallaxBlob = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroBannerY = useTransform(heroProgress, [0, 1], ["0%", "22%"]);
  const heroBannerScale = useTransform(heroProgress, [0, 1], [1.05, 1.18]);
  const heroContentY = useTransform(heroProgress, [0, 1], ["0%", "-12%"]);
  const heroContentOpacity = useTransform(heroProgress, [0, 0.65, 1], [1, 0.85, 0]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-[#060606] font-barlow text-white">
      <div className="wbc-landing-noise" aria-hidden />

      <ScrollProgress />
      <Navbar onRegister={openRegister} />
      <RegisterModal open={registerOpen} onClose={closeRegister} />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative isolate flex min-h-[100svh] flex-col overflow-hidden"
      >
        <motion.div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat will-change-transform"
          style={{
            backgroundImage: `url(${heroBanner})`,
            y: heroBannerY,
            scale: heroBannerScale,
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(180deg,rgba(6,2,4,0.55)_0%,rgba(6,2,4,0.25)_45%,rgba(6,2,4,0.92)_100%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_85%_55%_at_50%_55%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.55)_100%)]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-32 bg-gradient-to-t from-[#060606] via-[#060606]/70 to-transparent"
          aria-hidden
        />

        <motion.div
          className="relative z-[2] flex min-h-[100svh] flex-col px-6 pb-16 pt-28 will-change-transform md:px-10 md:pb-24 md:pt-36"
          style={{ y: heroContentY, opacity: heroContentOpacity }}
        >
          <motion.div
            className="mx-auto flex w-full max-w-6xl flex-1 flex-col items-start justify-center"
            initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3">
              <span className="h-px w-10 bg-wbc-gold/70" />
              <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.32em] text-wbc-gold-light md:text-xs md:tracking-[0.4em]">
                21 – 23 July 2026 · New Delhi
              </span>
            </div>

            <h1 className="font-bebas mt-7 leading-[0.86] tracking-[0.005em] text-white">
              <span className="block text-[clamp(2.8rem,9.5vw,8rem)]">WBC Amateur</span>
              <span className="block text-[clamp(2.8rem,9.5vw,8rem)]">Muaythai India</span>
            </h1>

            <p className="font-bebas mt-4 text-[clamp(1.6rem,3.6vw,3.2rem)] leading-none tracking-[0.08em] text-wbc-gold-light">
              Open Championship 2026
            </p>

            <p className="font-condensed mt-7 max-w-xl text-[11px] uppercase leading-relaxed tracking-[0.28em] text-white/55 md:text-xs md:tracking-[0.32em]">
              Talkatora Indoor Stadium · New Delhi · Hosted by WBC Amateur Muaythai India
            </p>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
              <button
                type="button"
                onClick={openRegister}
                className="font-condensed inline-flex items-center justify-center rounded-sm bg-wbc-gold-light px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-[#1a0509] transition hover:bg-wbc-gold md:text-sm md:tracking-[0.26em]"
              >
                Register Your Team
              </button>
              <a
                href="#about"
                className="font-condensed inline-flex items-center justify-center rounded-sm border border-white/40 px-8 py-4 text-xs font-bold uppercase tracking-[0.22em] text-white/85 transition hover:border-white hover:text-white md:text-sm md:tracking-[0.26em]"
              >
                Event Details
              </a>
            </div>
          </motion.div>

          <a
            href="#about"
            className="group relative z-[2] mx-auto flex shrink-0 flex-col items-center gap-2 self-center pb-2 pt-6 text-white/55 transition hover:text-wbc-gold-light"
            aria-label="Scroll to content"
          >
            <span className="font-condensed text-[10px] uppercase tracking-[0.4em]">Scroll</span>
            <span className="h-10 w-px bg-gradient-to-b from-white/55 to-transparent transition group-hover:from-wbc-gold-light" />
          </a>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <Marquee />

      {/* STATS BAR — 3 columns */}
      <section className="border-y border-white/[0.06] bg-[#0A0204]">
        <div className="mx-auto grid max-w-7xl grid-cols-3 divide-x divide-white/[0.07]">
          {stats.map((s) => (
            <div
              key={s.label}
              className="px-6 py-10 text-center md:px-8 md:py-14"
            >
              <div className="font-bebas text-3xl tracking-wide text-wbc-gold-light md:text-5xl">
                {s.num}
              </div>
              <div className="font-condensed mt-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/40 md:text-xs md:tracking-[0.36em]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="scroll-mt-24 bg-[#060606] px-6 py-32 md:px-10 md:py-44">
        <div className="mx-auto grid max-w-7xl items-stretch gap-12 lg:grid-cols-12 lg:gap-20">
          <motion.div
            className="lg:col-span-6"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <div className="relative h-[420px] w-full overflow-hidden md:h-[560px] lg:h-full lg:min-h-[600px]">
              <img
                src={asset1}
                alt="Inside the championship"
                className="h-full w-full object-cover"
                loading="lazy"
              />
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(6,2,4,0)_60%,rgba(6,2,4,0.6)_100%)]" />
            </div>
          </motion.div>

          <motion.div
            className="flex flex-col justify-center lg:col-span-6"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
              About the Event
            </span>
            <h2 className="font-bebas mt-6 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl lg:text-7xl">
              India&apos;s Premier Amateur Muaythai Championship
            </h2>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/55 md:text-lg">
              WBC Amateur Muaythai India — the National Representative Body of World Boxing Council Muaythai (Thailand) — brings the country&apos;s most elite amateur Muaythai stage to the capital. State teams, gyms, and invited clubs converge on Talkatora Indoor Stadium.
            </p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/45 md:text-lg">
              Three days of championship combat. One national platform every serious athlete has been training for.
            </p>
            <a
              href="#schedule"
              className="font-condensed mt-10 inline-flex items-center gap-3 self-start text-xs font-bold uppercase tracking-[0.3em] text-wbc-gold-light transition hover:text-wbc-gold"
            >
              View Full Schedule
              <span className="h-px w-12 bg-wbc-gold-light transition group-hover:w-16" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="gallery" className="scroll-mt-24 bg-[#040104] px-6 py-32 md:px-10 md:py-44">
        <motion.div
          className="mx-auto max-w-7xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="mb-16 flex flex-col items-start gap-4 md:mb-20 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold-light">
                Inside the Ring
              </span>
              <h2 className="font-bebas mt-4 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl lg:text-7xl">
                Where Champions Are Made
              </h2>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-white/45 md:text-base">
              The lights, the crowd, and the gold — a glimpse of what waits for you inside Talkatora.
            </p>
          </div>

          <div className="wbc-masonry">
            {[
              { src: asset5, alt: "Champion portrait", tall: true },
              { src: asset6, alt: "Stadium and ring" },
              { src: asset3, alt: "Athlete warmup" },
              { src: asset7, alt: "Fight action", tall: true },
              { src: asset2, alt: "Crowd energy" },
              { src: asset1, alt: "Medal ceremony" },
            ].map((g, i) => (
              <figure
                key={i}
                className={`group relative mb-4 break-inside-avoid overflow-hidden md:mb-6 ${
                  g.tall ? "wbc-masonry-tall" : "wbc-masonry-std"
                }`}
              >
                <img
                  src={g.src}
                  alt={g.alt}
                  className="block h-full w-full object-cover transition duration-700 group-hover:scale-[1.04]"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 bg-black/0 transition duration-500 group-hover:bg-black/45" />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-3xl text-wbc-gold-light opacity-0 transition duration-500 group-hover:opacity-100">
                  ◆
                </span>
              </figure>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" className="scroll-mt-24 bg-[#060606] px-6 py-32 md:px-10 md:py-44">
        <motion.div
          className="mx-auto max-w-5xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="mb-16 flex flex-col items-start gap-4 md:mb-24 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
                Competition Format
              </span>
              <h2 className="font-bebas mt-4 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl lg:text-7xl">
                Fight Your Category
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-white/45 md:text-base">
              From under-10 to senior — one stage. Pick your division and make the country remember your name.
            </p>
          </div>

          <ul className="border-t border-white/[0.07]">
            {categories.map((c) => (
              <motion.li
                key={c.code}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.5 }}
                className="group grid grid-cols-12 items-center gap-4 border-b border-white/[0.07] py-7 transition md:py-9"
              >
                <div className="col-span-3 md:col-span-2">
                  <span className="font-bebas text-3xl leading-none text-wbc-gold-light transition group-hover:text-wbc-gold md:text-5xl">
                    {c.code}
                  </span>
                </div>
                <div className="col-span-9 md:col-span-4">
                  <span className="font-condensed text-sm font-bold uppercase tracking-[0.18em] text-white md:text-base md:tracking-[0.22em]">
                    {c.name}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-6">
                  <p className="font-barlow text-sm italic leading-relaxed text-white/45 md:text-[15px]">
                    {c.hook}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </section>

      {/* KIT */}
      <section id="kit" className="scroll-mt-24 bg-[#0C0103]">
        <div className="mx-auto grid max-w-none grid-cols-1 lg:grid-cols-2">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="relative h-[360px] w-full overflow-hidden md:h-[520px] lg:h-auto lg:min-h-[640px]"
          >
            <img
              src={asset5}
              alt="Fighter ready in the ring"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(12,1,3,0.2)_0%,rgba(12,1,3,0.55)_100%)]" />
          </motion.div>

          <motion.div
            className="flex flex-col justify-center px-6 py-24 md:px-16 md:py-32 lg:px-24"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
              Official Event Kit
            </span>
            <h2 className="font-bebas mt-6 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl">
              Every Fighter Gets This
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-white/55 md:text-lg">
              Every registered competitor receives an exclusive Open Amateur Championship 2026 kit — designed to make you look and feel like a champion before you even step in the ring.
            </p>

            <ul className="mt-12 space-y-5">
              {kitItems.map((k) => (
                <li
                  key={k.text}
                  className="flex items-baseline gap-4 border-b border-white/[0.07] pb-4 font-condensed text-base font-semibold uppercase tracking-[0.14em] text-white/85 md:text-lg md:tracking-[0.18em]"
                >
                  <span className="text-wbc-gold-light">◆</span>
                  <span>{k.text}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section
        ref={scheduleRef}
        id="schedule"
        className="scroll-mt-24 relative overflow-hidden bg-[#030206] px-6 py-32 md:px-10 md:py-44"
      >
        <motion.div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-10%,rgba(214,0,28,0.18)_0%,transparent_55%)]"
          style={{ y: parallaxBlob }}
          aria-hidden
        />

        <div className="relative mx-auto max-w-5xl">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="mb-20 flex flex-col items-start gap-4 md:mb-28 md:flex-row md:items-end md:justify-between"
          >
            <div>
              <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
                Event Schedule
              </span>
              <h2 className="font-bebas mt-4 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl lg:text-7xl">
                Three Days.
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-white/45 md:text-base">
              Competition runs <span className="text-white/80">21–23 July</span> at Talkatora.{" "}
              <span className="text-white/80">20 July</span> is a separate intake day — full reporting flow ships in your{" "}
              <span className="text-wbc-gold-light">confirmation email</span>.
            </p>
          </motion.div>

          <div className="relative">
            {/* Spine */}
            <div
              className="absolute left-0 top-0 bottom-0 w-px overflow-hidden md:left-2"
              aria-hidden
            >
              <div className="absolute inset-0 bg-white/[0.07]" />
              <motion.div
                className="absolute inset-x-0 top-0 h-full origin-top bg-gradient-to-b from-wbc-gold-light via-wbc-gold/60 to-wbc-red/40"
                style={{ scaleY: spineProgress }}
              />
            </div>

            <motion.ul
              className="relative space-y-20 pl-8 md:space-y-28 md:pl-16"
              variants={timelineListVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1, margin: "-60px 0px -60px 0px" }}
            >
              {timeline.map((t) => (
                <motion.li
                  key={t.day}
                  variants={timelineItemVariants}
                  className="relative"
                >
                  <span
                    className="absolute -left-[2.15rem] top-3 h-3 w-3 rounded-full bg-wbc-gold-light shadow-[0_0_24px_rgba(212,160,23,0.55)] md:-left-[4.15rem]"
                    aria-hidden
                  />

                  <div className="relative">
                    <span
                      className="font-bebas pointer-events-none absolute -left-2 -top-10 select-none text-[clamp(8rem,22vw,18rem)] leading-[0.8] tracking-tight text-white/[0.045] md:-left-4 md:-top-16"
                      aria-hidden
                    >
                      {t.day}
                    </span>

                    <span className="font-condensed relative text-[10px] font-bold uppercase tracking-[0.4em] text-wbc-gold-light/80 md:text-xs">
                      {t.badge}
                    </span>
                    <h3 className="font-bebas relative mt-4 text-3xl leading-tight tracking-wide text-white md:text-5xl">
                      {t.title}
                    </h3>
                    <p className="font-condensed relative mt-4 max-w-2xl text-sm font-medium uppercase leading-snug tracking-[0.14em] text-wbc-red-bright/90 md:text-[15px]">
                      {t.subtitle}
                    </p>
                    <p className="relative mt-5 max-w-2xl font-barlow text-sm leading-relaxed text-white/55 md:text-base">
                      {t.body}
                    </p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </div>
      </section>

      {/* AWARDS */}
      <section
        id="awards"
        className="scroll-mt-24 relative overflow-hidden bg-[#060606] px-6 py-32 text-center md:px-10 md:py-44"
      >
        <div
          className="font-bebas pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none text-[clamp(140px,30vw,320px)] tracking-wide text-white/[0.035]"
          aria-hidden
        >
          GLORY
        </div>
        <motion.div
          className="relative mx-auto max-w-3xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold-light">
            Awards & Honours
          </span>
          <h2 className="font-bebas mt-4 text-5xl leading-[0.95] tracking-wide text-white md:text-7xl">
            Fight For Glory
          </h2>
          <p className="mx-auto mt-8 max-w-xl text-base leading-relaxed text-white/55 md:text-lg">
            Champions take the podium. Every finalist walks away with a medal. Every competitor earns a WBC official certificate.
          </p>
          <div className="mt-16 flex flex-wrap items-end justify-center gap-12">
            <Medal rank="1st" place="Place" label="Gold Medal" variant="gold" />
            <Medal rank="2nd" place="Place" label="Silver Medal" variant="silver" />
            <Medal rank="3rd" place="Place" label="Bronze ×2" variant="bronze" />
          </div>
        </motion.div>
      </section>

      {/* DOCUMENTS */}
      <section id="documents" className="scroll-mt-24 bg-[#0A0204] px-6 py-32 md:px-10 md:py-44">
        <motion.div
          className="mx-auto max-w-3xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          <div className="text-center">
            <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
              Mandatory Documents
            </span>
            <h2 className="font-bebas mt-4 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl">
              What to Bring
            </h2>
          </div>

          <ul className="mt-16 border-t border-white/[0.07]">
            {docs.map((d) => (
              <li
                key={d}
                className="flex items-baseline gap-4 border-b border-white/[0.07] py-5 font-barlow text-base text-white/80 md:text-lg"
              >
                <span className="text-wbc-red">◆</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>

          <p className="mt-12 text-center text-sm leading-relaxed text-white/35 md:text-base">
            Bring everything for the <span className="text-wbc-gold-light">20 July</span> intake (paperwork, medical, weigh-in). Exact reporting instructions come in your <span className="text-white/70">automatic confirmation email</span>. Athletes without complete documentation will not be permitted to compete.
          </p>

          <div className="mt-14 flex flex-col items-center gap-4 border-t border-white/[0.07] pt-12">
            <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
              Official Competition Rules
            </span>
            <p className="max-w-md text-center text-sm leading-relaxed text-white/45 md:text-base">
              Read the full WBC Muaythai rulebook before you step in the ring — scoring, equipment, conduct, and bout regulations.
            </p>
            <a
              href={RULES_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="font-condensed mt-2 inline-flex items-center gap-3 rounded-sm border border-wbc-gold-light/70 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.24em] text-wbc-gold-light transition hover:border-wbc-gold-light hover:bg-wbc-gold-light hover:text-[#1a0509] md:text-sm md:tracking-[0.28em]"
            >
              View Competition Rules
              <span aria-hidden>→</span>
            </a>
          </div>
        </motion.div>
      </section>

      {/* KEY DATES */}
      <section id="dates" className="scroll-mt-24 bg-[#060606] px-6 py-32 md:px-10 md:py-44">
        <motion.div
          className="mx-auto max-w-6xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.25 }}
        >
          <div className="text-center">
            <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-wbc-gold">
              Key Dates
            </span>
            <h2 className="font-bebas mt-4 text-5xl leading-[0.95] tracking-wide text-white md:text-6xl lg:text-7xl">
              Don&apos;t Miss These
            </h2>
          </div>

          {/* Desktop horizontal rail */}
          <div className="relative mt-24 hidden md:block">
            <div className="absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-wbc-gold/60 to-transparent" />
            <ol className="relative grid grid-cols-5">
              {keyDates.map((d) => (
                <li key={d.when} className="flex flex-col items-center text-center">
                  <div className="font-bebas text-3xl tracking-wide text-wbc-gold-light md:text-4xl">
                    {d.when}
                  </div>
                  <span className="my-4 h-3 w-px bg-wbc-gold-light" />
                  <p className="font-condensed max-w-[140px] text-[11px] font-semibold uppercase leading-snug tracking-[0.18em] text-white/65">
                    {d.line}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          {/* Mobile vertical list */}
          <ol className="relative mt-16 space-y-6 border-l border-wbc-gold/30 pl-6 md:hidden">
            {keyDates.map((d) => (
              <li key={d.when} className="relative">
                <span className="absolute -left-[1.55rem] top-2 h-2 w-2 rounded-full bg-wbc-gold-light" />
                <div className="font-bebas text-3xl text-wbc-gold-light">{d.when}</div>
                <p className="font-condensed mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/65">
                  {d.line}
                </p>
              </li>
            ))}
          </ol>
        </motion.div>
      </section>

      {/* REGISTER CTA */}
      <section
        id="register"
        className="scroll-mt-24 relative overflow-hidden bg-gradient-to-br from-wbc-red-dark via-wbc-red to-wbc-red-blood px-6 py-32 text-center md:px-10 md:py-44"
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpolygon points='30 0 60 30 30 60 0 30'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          aria-hidden
        />
        <motion.div
          className="relative z-[1] mx-auto max-w-3xl"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="font-condensed text-[11px] font-bold uppercase tracking-[0.4em] text-white/85">
            Entries Open Now
          </span>
          <h2 className="font-bebas mt-6 text-6xl leading-[0.9] tracking-wide text-white md:text-8xl lg:text-9xl">
            Your Ring Awaits
          </h2>
          <p className="mx-auto mt-10 max-w-xl text-base leading-relaxed text-white/85 md:text-lg">
            The bracket does not wait. Register before the team deadline — championship combat is{" "}
            <span className="font-semibold text-white">21–23 July</span>.
          </p>
          <button
            type="button"
            onClick={openRegister}
            className="font-condensed mt-12 inline-block bg-white px-12 py-5 text-sm font-bold uppercase tracking-[0.28em] text-wbc-red transition hover:bg-[#1a0509] hover:text-white md:text-base"
          >
            Register Now — Pick Your Role
          </button>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="relative bg-[#040104] px-6 py-20 md:px-10 md:py-24">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-wbc-red to-transparent"
          aria-hidden
        />
        <div className="mx-auto grid max-w-7xl gap-14 md:grid-cols-3 md:gap-16">
          <div>
            <img
              src={logo}
              alt="WBC Amateur Muaythai India"
              className="h-12 w-auto object-contain"
            />
            <p className="mt-8 text-sm leading-relaxed text-white/45">
              National Representative Body of
              <br />
              World Boxing Council Muaythai (Thailand)
            </p>
            <p className="mt-5 text-sm leading-relaxed text-white/45">
              Corporate Office: 1001, 10th Floor, NDM 1,
              <br />
              Netaji Subhash Place, Pitampura, New Delhi 110034
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-white/30">Regd. No. 952/2024</p>
          </div>
          <div>
            <h4 className="font-condensed text-[11px] font-bold uppercase tracking-[0.32em] text-wbc-gold">
              Contact
            </h4>
            <div className="mt-7 space-y-2 text-sm">
              <a href="tel:7503850120" className="block text-white/55 transition hover:text-wbc-gold-light">
                +91 7503850120
              </a>
              <a href="tel:9811227337" className="block text-white/55 transition hover:text-wbc-gold-light">
                +91 9811227337
              </a>
              <a href="tel:9205206059" className="block text-white/55 transition hover:text-wbc-gold-light">
                +91 9205206059
              </a>
              <a href="mailto:admin@wbcmuaythaiindia.com" className="block text-white/55 transition hover:text-wbc-gold-light">
                admin@wbcmuaythaiindia.com
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-condensed text-[11px] font-bold uppercase tracking-[0.32em] text-wbc-gold">
              Event Venue
            </h4>
            <p className="mt-7 text-sm leading-relaxed text-white/55">
              Talkatora Indoor Stadium
              <br />
              New Delhi, India
            </p>
            <p className="mt-4 text-sm text-white/55">
              Championship 21st – 23rd July 2026 · Intake 20th July
            </p>
            <a
              href={SITE}
              target="_blank"
              rel="noopener noreferrer"
              className="font-condensed mt-6 inline-block text-xs uppercase tracking-[0.24em] text-wbc-gold-light transition hover:text-wbc-gold"
            >
              www.wbcamateurmuaythaiindia.com
            </a>
            <a
              href={RULES_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="font-condensed mt-3 block text-xs uppercase tracking-[0.24em] text-white/55 transition hover:text-wbc-gold-light"
            >
              View Competition Rules (PDF)
            </a>
          </div>
        </div>
        <div className="mx-auto mt-16 flex max-w-7xl flex-wrap items-center justify-between gap-4 border-t border-white/[0.05] pt-10">
          <div className="font-bebas text-lg tracking-[0.18em] text-white/25">
            WBC AMATEUR MUAYTHAI INDIA · 2026
          </div>
          <p className="text-xs tracking-[0.14em] text-white/25">
            &copy; 2026 WBC Amateur Muaythai India. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Medal({ rank, place, label, variant }) {
  const styles = {
    gold: "border-wbc-gold-light text-wbc-gold-light",
    silver: "border-[#C8C8C8] text-[#DCDCDC]",
    bronze: "border-[#C87832] text-[#DC9846]",
  };
  const sizes = {
    gold: "h-[140px] w-[140px]",
    silver: "h-[120px] w-[120px]",
    bronze: "h-[120px] w-[120px]",
  };
  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className={`flex flex-col items-center justify-center rounded-full border-[2px] bg-black/30 ${styles[variant]} ${sizes[variant]}`}
      >
        <strong className="font-bebas text-3xl">{rank}</strong>
        <span className="font-condensed text-[10px] uppercase tracking-[0.3em] text-white/60">{place}</span>
      </div>
      <div className="font-condensed text-xs font-semibold uppercase tracking-[0.24em] text-white/85">
        {label}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
const _ImgPhKeep = ImgPh;